import {
  EMPTY_EXTENDED_ADDRESS,
  isAddressExtendedComplete
} from '@dadohr/shared/domainTypes/address';
import {
  ISubTaskSwagUpChoiceDenormalized,
  SubTaskStatus,
  SubTaskType,
  SwagUpChoiceAnswer,
  SwagUpChoiceAnswerData,
  SwagUpSwagChoice
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { MenuItem, Select } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { AddressExtendedForm } from '../../../../../components/AddessForm';
import { CompleteButton } from '../../../../../components/CompletionButtons';
import { RadioPanel } from '../../../../../components/RadioPanel';
import { useUnsafeTranslation } from '../../../../../i18next';
import { joinWithCommaAndOr } from '../../../../../services/enumerate';
import { trackCompletions } from '../../service';
import { SubTaskContext } from '../Task';
import { SubTaskButtonContainer } from './common';

const SwagImage = styled('img')((p) => ({
  maxWidth: '100%'
}));

const Container = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const Body = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2),
  alignItems: 'space-between',
  height: '100%'
}));

// We currently allow only a single selection,
// but predict that multi selection might eventually be a thing,
// hence this is already using an array of choices
export const SwagUpChoiceSelector = ({
  value,
  onChange,
  options,
  hidePhotos
}: {
  value: SwagUpChoiceAnswer[];
  onChange: (nextValue: SwagUpChoiceAnswer[]) => void;
  options: SwagUpSwagChoice[];
  hidePhotos?: boolean;
}) => {
  // radio panel
  const choice = value[0];
  return (
    <Container>
      <RadioPanel
        value={choice?.productId?.toString()}
        onChange={(productId) => {
          const nextChoice = options.find((o) => `${o.product}` === productId);
          if (nextChoice) {
            onChange([
              {
                productId: nextChoice.product,
                sizeId:
                  // E.g. if someone has chosen M, and there's M available on the other item, use that.
                  choice &&
                  !!nextChoice.sizes.find((s) => s.id === choice.sizeId)
                    ? choice.sizeId
                    : nextChoice.sizes[0].id
              }
            ]);
          }
        }}
        maxItemsBeforeWrap={2}
        options={options.map((o) => {
          return {
            label: o.product_name,
            body: (
              <Body>
                {!hidePhotos && <SwagImage src={o.product_image} />}
                <Select
                  style={{ alignSelf: 'flex-end' }}
                  value={
                    // E.g. if someone has chosen M, and there's M available on the other item, use that.
                    choice && !!o.sizes.find((s) => s.id === choice.sizeId)
                      ? choice.sizeId.toString()
                      : o.sizes[0]?.id?.toString()
                  }
                  onChange={(ev) => {
                    onChange([
                      {
                        productId: choice.productId,
                        sizeId: parseInt(ev.target.value as string)
                      }
                    ]);
                  }}
                  fullWidth
                >
                  {o.sizes.map((s) => (
                    <MenuItem key={s.id} value={s.id.toString()}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </Body>
            ),
            value: `${o.product}`
          };
        })}
      />
    </Container>
  );
};

export const SubTaskSwagUpChoice = ({
  context,
  s,
  onSubmitDone,
  status,
  highlight,
  mode
}: {
  s: ISubTaskSwagUpChoiceDenormalized;
  context: SubTaskContext;
  onSubmitDone: () => void;
  status: SubTaskStatus;
  highlight: boolean;
  mode: 'LIVE' | 'PREVIEW';
}) => {
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [state, setState] = useState<SwagUpChoiceAnswerData>(
    s.data.answer || {
      type: SubTaskType.SWAG_UP_CHOICE,
      choices: [
        {
          productId: s.data.question.choices[0].product,
          sizeId: s.data.question.choices[0].sizes[0].id
        }
      ],
      shippingAddress: EMPTY_EXTENDED_ADDRESS()
    }
  );

  const isFullyFilledOut = useMemo(() => {
    return (
      state.choices.length && isAddressExtendedComplete(state.shippingAddress)
    );
  }, [state]);

  return (
    <>
      <SwagUpChoiceSelector
        value={state.choices}
        onChange={(choices) => setState((s) => ({ ...s, choices }))}
        options={s.data.question.choices}
        hidePhotos={s.data.question.opts.hidePhotos}
      />

      <AddressExtendedForm
        value={state.shippingAddress}
        onChange={(shippingAddress) =>
          setState((s) => ({ ...s, shippingAddress }))
        }
      />

      <SubTaskButtonContainer>
        <CompleteButton
          disabled={
            status === SubTaskStatus.DONE ||
            status === SubTaskStatus.SKIPPED ||
            !isFullyFilledOut
          }
          onClick={async () => {
            if (mode === 'PREVIEW') {
              return;
            }
            return context.answerComplexSubtask(s.id, state).then((res) => {
              console.log(res);
              if (res.status === 'OK') {
                enqueueSnackbar(
                  t('Your swag has been been successfully ordered!'),
                  {
                    variant: 'success'
                  }
                );
                trackCompletions(res.completions, context.trackEvent);
                onSubmitDone();
              }
              if (res.status === 'ERROR') {
                if (res.reason === 'OUT_OF_STOCK') {
                  enqueueSnackbar(
                    t(
                      'Unfortunately this item is out of stock - please choose something else or get in touch with {{ contacts }}.',
                      {
                        contacts: joinWithCommaAndOr(
                          s.data.question.swagUpContactEmails,
                          t
                        )
                      }
                    ),
                    {
                      variant: 'error'
                    }
                  );
                } else if (res.reason === 'SIZE_NOT_AVAILABLE') {
                  enqueueSnackbar(
                    t(
                      'Unfortunately this size is unavailable right now. Please choose from the available sizes for this item ({{ availableSizes }}), or check other items to see if your desired size is available.',
                      {
                        availableSizes: (res.data?.availableSizes || []).join(
                          ', '
                        )
                      }
                    ),
                    {
                      variant: 'error'
                    }
                  );
                } else {
                  enqueueSnackbar(
                    t(
                      'Something went wrong - please try again or contact {{ contacts }}.',
                      {
                        contacts: joinWithCommaAndOr(
                          s.data.question.swagUpContactEmails,
                          t
                        )
                      }
                    ),
                    {
                      variant: 'error'
                    }
                  );
                }
              }
            });
          }}
          status={status}
          mode={mode}
        >
          {t('Submit choice')}
        </CompleteButton>
      </SubTaskButtonContainer>
    </>
  );
};
